import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  LinkIcon,
  ShoppingCartIcon,
  CloudArrowUpIcon,
  KeyIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { apiService } from "../services/apiService";

type Step = 1 | 2 | 3 | 4;
type InstallMode = "auto" | "manual";

const POLL_INTERVAL_MS = 5000;
const POLL_MAX_MS = 30000;

interface WooCommerceConnectWizardProps {
  onConnected?: () => void;
  initialStoreUrl?: string;
  oauthState?: string | null;
}

function formatVerifyError(res: {
  error?: string;
  message?: string;
  code?: string;
  wordpressProbe?: { message?: string; code?: string };
}): string {
  const msg = res.message || res.error || "";
  const code = res.code || "";
  if (msg && !/error desconocido/i.test(msg)) return msg;
  if (code === "NOT_WORDPRESS" || res.wordpressProbe?.code === "NOT_WORDPRESS") {
    return (
      res.wordpressProbe?.message ||
      "Esta URL no es WordPress. Use la URL donde está instalado WooCommerce."
    );
  }
  if (code === "PLUGIN_NOT_FOUND") return "El plugin FactuApp no responde. Actívelo en WordPress → Plugins.";
  if (code === "PLUGIN_NOT_LINKED") return "Plugin instalado pero sin vincular. Pulse «Conectar con FactuApp» en WordPress.";
  if (code === "OAUTH_AWAITING_WP") return "OAuth aprobado en FactuApp. Complete el paso en WordPress.";
  if (code === "NO_SITE_TOKEN") return "Falta el token de sitio. Finalice OAuth en WordPress.";
  if (code === "TIMEOUT") return "No se pudo contactar la tienda. Compruebe que el plugin está activo.";
  if (code === "SSL_ERROR") return "Error de certificado SSL al contactar la tienda.";
  return msg || "No se pudo verificar la conexión. Compruebe la URL de la tienda.";
}

const inputCls =
  "w-full border border-zinc-200/80 dark:border-zinc-700 rounded-xl px-4 py-2.5 text-sm bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 focus:ring-2 focus:ring-violet-500/30 outline-none transition-colors";

const btnPrimary =
  "w-full py-2.5 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

const btnSecondary =
  "w-full py-2.5 rounded-xl border border-zinc-200 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 text-sm font-medium hover:bg-zinc-50 dark:hover:bg-zinc-800 disabled:opacity-50 transition-colors";

export const WooCommerceConnectWizard: React.FC<WooCommerceConnectWizardProps> = ({
  onConnected,
  initialStoreUrl = "",
  oauthState = null,
}) => {
  const [step, setStep] = useState<Step>(1);
  const [storeUrl, setStoreUrl] = useState(initialStoreUrl);
  const [installMode, setInstallMode] = useState<InstallMode>("auto");
  const [wpUser, setWpUser] = useState("");
  const [wpAppPassword, setWpAppPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [installStatus, setInstallStatus] = useState<"idle" | "installing" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [pluginUrl, setPluginUrl] = useState<string | null>(null);
  const [oauthPending, setOauthPending] = useState<string | null>(oauthState);
  const [verified, setVerified] = useState(false);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [canMarkConnected, setCanMarkConnected] = useState(false);
  const pollStartedAt = useRef<number | null>(null);

  useEffect(() => {
    if (initialStoreUrl) setStoreUrl(initialStoreUrl);
  }, [initialStoreUrl]);

  useEffect(() => {
    if (oauthState) {
      setOauthPending(oauthState);
      setStep(3);
    }
  }, [oauthState]);

  useEffect(() => {
    (async () => {
      const res = await apiService.getWooCommercePluginInfo();
      const d = res.data as { downloadUrl?: string };
      if (d?.downloadUrl) setPluginUrl(d.downloadUrl);
    })();
  }, []);

  const normalizedStore = storeUrl.trim().replace(/\/+$/, "");
  const wpAdminUrl = normalizedStore
    ? `${normalizedStore}/wp-admin/admin.php?page=factuapp`
    : "";

  // ── Auto-install plugin ──────────────────────────────────────────────────
  const handleAutoInstall = async () => {
    if (!normalizedStore || !wpUser.trim() || !wpAppPassword.trim()) {
      setError("Completa la URL de la tienda, el usuario y la contraseña de aplicación.");
      return;
    }
    setBusy(true);
    setInstallStatus("installing");
    setError(null);
    setMessage(null);

    const res = await apiService.installWooCommercePlugin({
      storeUrl: normalizedStore,
      wpUser: wpUser.trim(),
      wpAppPassword: wpAppPassword.trim(),
    });

    setBusy(false);

    if (res.error) {
      const d = res.data as { hint?: string } | null;
      setInstallStatus("error");
      setError((res.message || res.error) + (d?.hint ? `\n${d.hint}` : ""));
      return;
    }

    const d = res.data as { ok?: boolean; status?: string; message?: string };
    if (d?.ok) {
      setInstallStatus("success");
      setMessage(d.message || "Plugin instalado y activado correctamente.");
      // Go straight to OAuth step
      setTimeout(() => setStep(3), 800);
    } else {
      setInstallStatus("error");
      setError(d?.message || "Error desconocido al instalar el plugin.");
    }
  };

  // ── OAuth ─────────────────────────────────────────────────────────────────
  const approveOAuth = useCallback(async () => {
    if (!oauthPending) return;
    setBusy(true);
    setError(null);

    const statusRes = await apiService.getWooCommerceOAuthStatus(oauthPending);
    const status = statusRes.data as { valid?: boolean; expired?: boolean };
    if (statusRes.error) {
      setBusy(false);
      setError(statusRes.message || statusRes.error);
      return;
    }
    if (!status?.valid || status?.expired) {
      setBusy(false);
      setError("La sesión OAuth ha expirado (15 min). Vuelva a pulsar «Conectar con FactuApp» en WordPress.");
      return;
    }

    const res = await apiService.approveWooCommerceOAuth(oauthPending);
    setBusy(false);
    if (res.error) {
      const msg = res.message || res.error;
      setError(/expirad/i.test(msg)
        ? "La sesión OAuth expiró. Reinicie la conexión desde WordPress."
        : /otra empresa/i.test(msg)
        ? "Esta conexión pertenece a otra empresa de FactuApp."
        : msg);
      return;
    }
    const d = res.data as { wpRedirectUrl?: string; message?: string };
    setMessage(d?.message || "Aprobado. Complete el paso en WordPress.");
    if (d?.wpRedirectUrl) window.open(d.wpRedirectUrl, "_blank", "noopener,noreferrer");
    setCanMarkConnected(true);
    setStep(4);
  }, [oauthPending]);

  useEffect(() => {
    if (step === 3 && oauthPending) void approveOAuth();
  }, [step, oauthPending, approveOAuth]);

  const startOAuthFromFactuApp = () => {
    if (!normalizedStore) {
      setError("Indica la URL de tu tienda en el paso 1.");
      setStep(1);
      return;
    }
    setError(null);
    setPollTimedOut(false);
    pollStartedAt.current = null;
    const url = apiService.getWooCommerceAuthorizeUrl(normalizedStore);
    window.open(url, "_blank", "noopener,noreferrer");
    setMessage("Aprueba la conexión en FactuApp y vuelve a WordPress para finalizar.");
    setCanMarkConnected(true);
    setStep(4);
  };

  // ── Verify ────────────────────────────────────────────────────────────────
  const pollVerify = useCallback(async () => {
    setBusy(true);
    setError(null);
    const res = await apiService.verifyWooCommercePlugin();
    setBusy(false);
    const payload = res.data as {
      ok?: boolean;
      code?: string;
      canMarkConnected?: boolean;
      wordpressProbe?: { message?: string; code?: string };
    };
    if (res.error || payload?.ok === false) {
      setError(formatVerifyError({ error: res.error, message: res.message, code: payload?.code || res.code, wordpressProbe: payload?.wordpressProbe }));
      setCanMarkConnected(!!payload?.canMarkConnected);
      setVerified(false);
      return false;
    }
    if ((res.data as { ok?: boolean })?.ok) {
      setVerified(true);
      setPollTimedOut(false);
      setError(null);
      setMessage("Conexión verificada con tu tienda WordPress.");
      onConnected?.();
      return true;
    }
    return false;
  }, [onConnected]);

  useEffect(() => {
    if (step !== 4 || verified) return;
    if (pollStartedAt.current === null) {
      pollStartedAt.current = Date.now();
      setPollTimedOut(false);
      void pollVerify();
    }
    const t = setInterval(() => {
      const elapsed = Date.now() - (pollStartedAt.current || Date.now());
      if (elapsed >= POLL_MAX_MS) {
        setPollTimedOut(true);
        setError((prev) => prev || "Verificación detenida tras 30 s. Compruebe que el plugin está activo y que completó el OAuth en WordPress.");
        return;
      }
      void pollVerify();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [step, verified, pollVerify]);

  useEffect(() => {
    if (step !== 4) {
      pollStartedAt.current = null;
      setPollTimedOut(false);
    }
  }, [step]);

  const handleMarkConnected = async () => {
    setBusy(true);
    setError(null);
    const res = await apiService.markWooCommerceConnected(normalizedStore || undefined);
    setBusy(false);
    if (res.error) { setError(res.message || res.error); return; }
    const d = res.data as { message?: string; storeUrl?: string };
    setMessage(d?.message || "Marcado como conectado en FactuApp.");
    if (d?.storeUrl) setStoreUrl(d.storeUrl);
    await pollVerify();
  };

  const stepLabels: Record<Step, string> = { 1: "Tienda", 2: "Plugin", 3: "Conectar", 4: "Verificar" };

  return (
    <div className="rounded-2xl border border-white/60 bg-white/70 dark:bg-zinc-900/70 backdrop-blur-xl shadow-lg p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="rounded-2xl bg-violet-500/10 p-3 ring-1 ring-violet-500/20">
          <ShoppingCartIcon className="h-8 w-8 text-violet-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 tracking-tight">
            Conectar tienda WooCommerce
          </h3>
          <p className="text-sm text-zinc-500">Instalación automática · OAuth · sin claves manuales</p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex flex-wrap gap-2 text-xs font-medium">
        {([1, 2, 3, 4] as Step[]).map((s) => (
          <span
            key={s}
            className={`px-3 py-1 rounded-full transition-colors ${
              step === s
                ? "bg-violet-600 text-white"
                : step > s
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                : "bg-zinc-100 dark:bg-zinc-800 text-zinc-500"
            }`}
          >
            {step > s ? "✓ " : ""}{stepLabels[s]}
          </span>
        ))}
      </div>

      {/* Notifications */}
      {error && (
        <div className="flex gap-2 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-xl px-3 py-2.5 whitespace-pre-line">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {message && (
        <div className="flex gap-2 text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl px-3 py-2.5">
          <CheckCircleIcon className="h-5 w-5 shrink-0" />
          {message}
        </div>
      )}

      {/* ── STEP 1: Store URL ─────────────────────────────────────────────── */}
      {step === 1 && (
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium text-zinc-700 dark:text-zinc-300 block mb-1.5">
              URL de tu tienda WordPress
            </label>
            <input
              type="url"
              placeholder="https://mitienda.com"
              value={storeUrl}
              onChange={(e) => setStoreUrl(e.target.value)}
              className={inputCls}
            />
            <p className="text-xs text-zinc-400 mt-1">
              La URL raíz de WordPress donde está WooCommerce (responde en /wp-json).
            </p>
          </div>
          <button
            type="button"
            disabled={!storeUrl.trim()}
            onClick={() => { setError(null); setMessage(null); setStep(2); }}
            className={btnPrimary}
          >
            Continuar →
          </button>
        </div>
      )}

      {/* ── STEP 2: Install plugin ────────────────────────────────────────── */}
      {step === 2 && (
        <div className="space-y-4">
          {/* Mode selector */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setInstallMode("auto")}
              className={`rounded-xl border p-3 text-left transition-colors ${
                installMode === "auto"
                  ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20"
                  : "border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              }`}
            >
              <CloudArrowUpIcon className="h-5 w-5 text-violet-600 mb-1" />
              <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">Auto-instalar</p>
              <p className="text-xs text-zinc-500 mt-0.5">Con tus credenciales WP</p>
            </button>
            <button
              type="button"
              onClick={() => setInstallMode("manual")}
              className={`rounded-xl border p-3 text-left transition-colors ${
                installMode === "manual"
                  ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20"
                  : "border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800"
              }`}
            >
              <KeyIcon className="h-5 w-5 text-zinc-500 mb-1" />
              <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200">Manual</p>
              <p className="text-xs text-zinc-500 mt-0.5">Subir ZIP desde WP admin</p>
            </button>
          </div>

          {/* Auto-install form */}
          {installMode === "auto" && (
            <div className="space-y-3">
              <div className="text-xs text-zinc-500 bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 flex gap-2">
                <span>💡</span>
                <span>
                  Necesitas una <strong>Contraseña de Aplicación</strong> de WordPress:<br />
                  WP Admin → Usuarios → Tu perfil → <em>Contraseñas de aplicación</em> → «Añadir nueva».
                </span>
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 uppercase tracking-wide block mb-1">
                  Usuario WordPress (admin)
                </label>
                <input
                  type="text"
                  placeholder="admin"
                  value={wpUser}
                  autoComplete="username"
                  onChange={(e) => setWpUser(e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className="text-xs font-medium text-zinc-500 uppercase tracking-wide block mb-1">
                  Contraseña de aplicación
                </label>
                <input
                  type="password"
                  placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                  value={wpAppPassword}
                  autoComplete="new-password"
                  onChange={(e) => setWpAppPassword(e.target.value)}
                  className={`${inputCls} font-mono tracking-widest`}
                />
                <p className="text-xs text-zinc-400 mt-1">
                  Formato: 24 caracteres con espacios (ej. «AbCd EfGh 1234 xYzW 5678 QrSt»).
                  No uses tu contraseña de acceso de WP.
                </p>
              </div>

              {installStatus === "success" ? (
                <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-400 font-medium py-2">
                  <CheckCircleIcon className="h-5 w-5" />
                  Plugin instalado — pasando al siguiente paso…
                </div>
              ) : (
                <button
                  type="button"
                  disabled={busy || !wpUser.trim() || !wpAppPassword.trim()}
                  onClick={handleAutoInstall}
                  className={btnPrimary}
                >
                  {busy && installStatus === "installing"
                    ? "Instalando plugin…"
                    : "Instalar y activar plugin automáticamente"}
                </button>
              )}
            </div>
          )}

          {/* Manual install */}
          {installMode === "manual" && (
            <div className="space-y-3 text-sm text-zinc-600 dark:text-zinc-400">
              <ol className="list-decimal list-inside space-y-1.5">
                <li>Descarga el ZIP del plugin.</li>
                <li>Ve a <strong>WordPress → Plugins → Añadir nuevo → Subir plugin</strong>.</li>
                <li>Sube el ZIP, instala y activa <strong>Entikmedia FactuApp Connector</strong>.</li>
              </ol>
              {pluginUrl && (
                <a
                  href={pluginUrl}
                  download
                  className="inline-flex items-center gap-2 text-violet-600 font-medium hover:underline text-sm"
                >
                  <CloudArrowUpIcon className="h-4 w-4" />
                  Descargar factuapp-connector.zip
                </a>
              )}
              <button
                type="button"
                onClick={() => { setStep(3); }}
                className={btnPrimary}
              >
                Ya instalé el plugin →
              </button>
            </div>
          )}

          <button type="button" onClick={() => { setStep(1); setInstallStatus("idle"); setError(null); }} className="text-xs text-zinc-400 hover:text-zinc-600 transition-colors">
            ← Cambiar URL
          </button>
        </div>
      )}

      {/* ── STEP 3: OAuth ─────────────────────────────────────────────────── */}
      {step === 3 && (
        <div className="space-y-3 text-sm text-zinc-600 dark:text-zinc-400">
          <p>
            Pulsa para autorizar FactuApp con esta tienda. También puedes pulsar{" "}
            <strong>«Conectar con FactuApp»</strong> dentro del panel WordPress del plugin.
          </p>
          <button
            type="button"
            disabled={busy}
            onClick={startOAuthFromFactuApp}
            className={btnPrimary}
          >
            <span className="inline-flex items-center justify-center gap-2">
              <LinkIcon className="h-4 w-4" />
              Conectar con FactuApp (OAuth)
            </span>
          </button>
          {wpAdminUrl && (
            <a
              href={wpAdminUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-violet-600 hover:underline"
            >
              <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              Abrir panel WordPress del plugin
            </a>
          )}
          {oauthPending && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void approveOAuth()}
              className={btnSecondary}
            >
              {busy ? "Aprobando…" : "Reintentar aprobación OAuth"}
            </button>
          )}
          <button
            type="button"
            onClick={() => { setStep(2); setError(null); }}
            className="text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
          >
            ← Volver al plugin
          </button>
        </div>
      )}

      {/* ── STEP 4: Verify ────────────────────────────────────────────────── */}
      {step === 4 && (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600 dark:text-zinc-400">
            {verified ? (
              <span className="font-medium text-emerald-700 dark:text-emerald-400 flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5" />
                Tienda vinculada correctamente.
              </span>
            ) : pollTimedOut ? (
              <>Verificación detenida. Compruebe que el plugin está activo en WordPress.</>
            ) : (
              <>Comprobando el plugin en <span className="font-medium">{normalizedStore || "tu tienda"}</span>…</>
            )}
          </p>

          {!verified && (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  pollStartedAt.current = Date.now();
                  setPollTimedOut(false);
                  setError(null);
                  void pollVerify();
                }}
                className={btnPrimary}
              >
                {busy ? "Comprobando…" : "Verificar ahora"}
              </button>

              {wpAdminUrl && (
                <a
                  href={wpAdminUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-violet-600 hover:underline w-full justify-center"
                >
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  Abrir FactuApp en WordPress
                </a>
              )}

              {(canMarkConnected || pollTimedOut) && (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void handleMarkConnected()}
                  className={btnSecondary}
                >
                  Marcar como conectado (OAuth aprobado)
                </button>
              )}
            </>
          )}
        </div>
      )}

      <p className="text-[11px] text-zinc-400 text-center pt-2 border-t border-zinc-100 dark:border-zinc-800">
        Entikmedia · FactuApp · Plugin v2.1.0
      </p>
    </div>
  );
};

export default WooCommerceConnectWizard;
